import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from './ui/notify';
import './WheelchairRental.css';
import { useTranslation, LANGUAGES } from './i18n/LanguageContext';
import ImpressumFooter from './components/ImpressumFooter';

const WheelchairRental = () => {
  const { t, language, setLanguage } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');
  const [availableWheelchairs, setAvailableWheelchairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openingHours, setOpeningHours] = useState([]);
  
  // Oktoberfest-Zeitraum: 19.09.2026 bis 04.10.2026
  const OKTOBERFEST_START = '19092026';
  const OKTOBERFEST_END = '04102026';
  
  // Buchungsformular
  const [bookingData, setBookingData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    notes: ''
  });
  
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    fetchStations();
    fetchOpeningHours();
  }, []);

  // Automatisch Verfügbarkeit prüfen wenn Endzeit gewählt wird
  useEffect(() => {
    if (selectedDate && selectedStartTime && selectedEndTime && selectedEndTime > selectedStartTime) {
      checkAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndTime]);

  const fetchStations = async () => {
    try {
      const response = await fetch('/api/wheelchair/stations');
      const data = await response.json();
      setStations(data);
    } catch (error) {
      console.error('Fehler beim Laden der Stationen:', error);
    }
  };

  const fetchOpeningHours = async () => {
    try {
      const response = await fetch('/api/wheelchair/opening-hours');
      const data = await response.json();
      
      // Prüfen ob die Antwort ein Array oder Objekt ist
      if (Array.isArray(data)) {
        setOpeningHours(data);
      } else if (data && typeof data === 'object') {
        // Objekt in Array umwandeln
        const hoursArray = Object.keys(data).map((day, index) => ({
          id: index + 1,
          day_of_week: getDayOfWeekNumber(day),
          opening_time: data[day].open,
          closing_time: data[day].close,
          is_holiday: false,
          closed: data[day].closed || false
        }));
        setOpeningHours(hoursArray);
      } else {
        setOpeningHours([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Öffnungszeiten:', error);
      setOpeningHours([]);
    }
  };

  const getDayOfWeekNumber = (dayName) => {
    const days = {
      'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
      'thursday': 4, 'friday': 5, 'saturday': 6
    };
    return days[dayName] || 0;
  };

  // Generiere verfügbare Oktoberfest-Tage
  const getOktoberfestDays = () => {
    const days = [];
    const start = new Date(2026, 8, 19); // September ist Monat 8 (0-basiert)
    const end = new Date(2026, 9, 4);   // Oktober ist Monat 9
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear());
      const ttmmyyyy = `${day}${month}${year}`;
      
      const dayNames = [t('day.sunday'), t('day.monday'), t('day.tuesday'), t('day.wednesday'), t('day.thursday'), t('day.friday'), t('day.saturday')];
      const dayName = dayNames[date.getDay()];
      
      days.push({
        ttmmyyyy,
        display: `${day}.${month}.${year}`,
        dayName,
        date: new Date(date)
      });
    }
    return days;
  };

  // Verfügbare Zeiten basierend auf Öffnungszeiten
  const getTimeOptions = (dayOfWeek, isEndTime = false) => {
    const times = [];
    let startHour = 9; // Standard Mo-Fr
    
    if (dayOfWeek === 6) startHour = 7; // Samstag
    if (dayOfWeek === 0) startHour = 8; // Sonntag

    // Für den heutigen Tag: nur zukünftige Zeiten anzeigen
    const now = new Date();
    const selectedDayObj = getOktoberfestDays().find(d => d.ttmmyyyy === selectedDate);
    const isToday = selectedDayObj && 
      selectedDayObj.date.getDate() === now.getDate() &&
      selectedDayObj.date.getMonth() === now.getMonth() &&
      selectedDayObj.date.getFullYear() === now.getFullYear();
    
    const minHour = isToday ? now.getHours() + 1 : startHour;
    const effectiveStart = Math.max(startHour, minHour);

    // Bei Endzeit: nur Zeiten nach der Startzeit anzeigen
    const startTimeHour = isEndTime && selectedStartTime 
      ? parseInt(selectedStartTime.split(':')[0], 10) + 1
      : 0;
    const finalStart = Math.max(effectiveStart, startTimeHour);
    
    for (let hour = finalStart; hour <= 23; hour++) {
      times.push({
        value: `${String(hour).padStart(2, '0')}:00`,
        display: `${String(hour).padStart(2, '0')}:00 ${t('wr.clock')}`.trim()
      });
    }
    // Ende des Tages
    if (finalStart <= 24) {
      times.push({
        value: '24:00',
        display: t('wr.endOfDay')
      });
    }
    return times;
  };

  // Prüfen ob ein Tag in der Vergangenheit liegt
  const isDayPast = (dayDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dayDate);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const checkAvailability = async () => {
    if (!selectedDate || !selectedStartTime || !selectedEndTime) {
      notify(t('wr.selectDateTime'));
      return;
    }

    // Endzeit muss nach Startzeit liegen
    if (selectedEndTime <= selectedStartTime) {
      notify(t('wr.endAfterStart'));
      return;
    }

    setLoading(true);
    try {
      const endDate = selectedDate;
      
      const response = await fetch(
        `/api/wheelchair/available-all?startDate=${selectedDate}&startTime=${selectedStartTime}&endDate=${endDate}&endTime=${selectedEndTime}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAvailableWheelchairs(data);
      } else {
        const error = await response.json();
        notify(`Fehler: ${error.error}`);
        setAvailableWheelchairs([]);
      }
    } catch (error) {
      console.error('Fehler bei Verfügbarkeitsprüfung:', error);
        notify(t('wr.availError'));
    } finally {
      setLoading(false);
    }
  };

  const createBooking = async (wheelchairId, wheelchair) => {
    if (!bookingData.customerName) {
      notify(t('wr.enterNameAlert'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/wheelchair/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wheelchairId,
          customerName: bookingData.customerName,
          customerPhone: bookingData.customerPhone,
          customerEmail: bookingData.customerEmail,
          startDate: selectedDate,
          startTime: selectedStartTime,
          endDate: selectedDate,
          endTime: selectedEndTime,
          notes: bookingData.notes
        })
      });

      if (response.ok) {
        const result = await response.json();
        setBookingResult({
          success: true,
          message: t('wr.bookingSuccessMsg', { date: getSelectedDateDisplay() }),
          bookingId: result.bookingId,
          details: {
            station: wheelchair.station_name || '',
            date: getSelectedDateDisplay(),
            startTime: selectedStartTime,
            endTime: selectedEndTime,
            customerName: bookingData.customerName,
            wheelchairNumber: wheelchair.wheelchair_number
          }
        });
        // Reset form
        setBookingData({
          customerName: '',
          customerPhone: '',
          customerEmail: '',
          notes: ''
        });
        setAvailableWheelchairs([]);
        setSelectedDate('');
        setSelectedStartTime('');
        setSelectedEndTime('');
      } else {
        const error = await response.json();
        setBookingResult({
          success: false,
          message: `Fehler: ${error.error}`
        });
      }
    } catch (error) {
      console.error('Fehler bei Buchung:', error);
      setBookingResult({
        success: false,
        message: t('wr.bookingErrorMsg')
      });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedDateDisplay = () => {
    const day = getOktoberfestDays().find(d => d.ttmmyyyy === selectedDate);
    return day ? `${day.dayName}, ${day.display}` : selectedDate;
  };

  const getOpeningHoursText = () => {
    if (!Array.isArray(openingHours) || openingHours.length === 0) return null;

    const dayNames = [t('day.sunShort'), t('day.monShort'), t('day.tueShort'), t('day.wedShort'), t('day.thuShort'), t('day.friShort'), t('day.satShort')];
    const regularHours = openingHours.filter(oh => !oh.is_holiday && !oh.closed);
    
    if (regularHours.length === 0) return null;

    return (
      <div className="opening-hours">
        <h4>{t('wr.hours')}</h4>
        <ul>
          {regularHours.map(oh => (
            <li key={oh.id}>
              <strong>{dayNames[oh.day_of_week === 7 ? 0 : oh.day_of_week]}:</strong> {oh.opening_time} - {oh.closing_time === '24:00' ? '24:00' : oh.closing_time} {t('wr.clock')}
            </li>
          ))}
          <li className="period-info">
            <strong>{t('wr.period', { year: '2026' })}</strong>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <div className="wheelchair-rental">
      <div className="container" style={{ position: 'relative' }}>
        <h1>{t('wr.title', { year: '2026' })}</h1>
        
        {/* Language Switcher */}
        <div style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 100 }}>
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            style={{ background: 'rgba(255,255,255,0.9)', border: '2px solid #ddd', borderRadius: '8px', padding: '6px 12px', fontSize: '1.2rem', cursor: 'pointer' }}
          >
            {LANGUAGES.find(l => l.code === language)?.flag || '🇩🇪'}
          </button>
          {showLangMenu && (
            <div style={{ position: 'absolute', right: 0, top: '100%', background: 'white', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '160px', zIndex: 101 }}>
              {LANGUAGES.map(lang => (
                <div
                  key={lang.code}
                  onClick={() => { setLanguage(lang.code); setShowLangMenu(false); }}
                  style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', background: language === lang.code ? '#e8f0fe' : 'transparent' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseOut={e => e.currentTarget.style.background = language === lang.code ? '#e8f0fe' : 'transparent'}
                >
                  <span>{lang.flag}</span> <span>{lang.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {getOpeningHoursText()}
        
        {bookingResult && (
          <div className={`booking-result ${bookingResult.success ? 'success' : 'error'}`}>
            <h3>{bookingResult.success ? t('wr.bookingSuccess') : t('wr.bookingError')}</h3>
            <p>{bookingResult.message}</p>
            {bookingResult.success && bookingResult.details && (
              <div className="booking-confirmation-details" style={{ 
                background: 'rgba(255,255,255,0.9)', 
                borderRadius: '8px', 
                padding: '15px', 
                margin: '15px 0',
                textAlign: 'left'
              }}>
                <p><strong>{t('wr.bookingNum')}</strong> #{bookingResult.bookingId}</p>
                <p><strong>{t('wr.bookingName')}</strong> {bookingResult.details.customerName}</p>
                <p><strong>{t('wr.bookingWheelchair')}</strong> {bookingResult.details.wheelchairNumber}</p>
                <p><strong>{t('wr.bookingStation')}</strong> {bookingResult.details.station}</p>
                <p><strong>{t('wr.bookingDate')}</strong> {bookingResult.details.date}</p>
                <p><strong>{t('wr.bookingTime')}</strong> {bookingResult.details.startTime} - {bookingResult.details.endTime} {t('wr.clock')}</p>
                <button 
                  onClick={() => {
                    const text = `${t('wr.bookingWheelchair')} #${bookingResult.bookingId}\n${t('wr.bookingName')} ${bookingResult.details.customerName}\n${t('wr.bookingWheelchair')} ${bookingResult.details.wheelchairNumber}\n${t('wr.bookingStation')} ${bookingResult.details.station}\n${t('wr.bookingDate')} ${bookingResult.details.date}\n${t('wr.bookingTime')} ${bookingResult.details.startTime} - ${bookingResult.details.endTime} ${t('wr.clock')}`.trim();
                    navigator.clipboard.writeText(text).then(() => notify(t('wr.copied')));
                  }}
                  style={{ marginRight: '10px' }}
                >
                  {t('wr.copyDetails')}
                </button>
              </div>
            )}
            {bookingResult.success && (
              <button onClick={() => setBookingResult(null)}>{t('wr.newBooking')}</button>
            )}
          </div>
        )}

        {!bookingResult && (
          <>
            <div className="form-section">
              <h2>{t('wr.step1')}</h2>
              <div className="days-grid">
                {getOktoberfestDays().map(day => {
                  const past = isDayPast(day.date);
                  return (
                    <div 
                      key={day.ttmmyyyy}
                      className={`day-card ${selectedDate === day.ttmmyyyy ? 'selected' : ''} ${past ? 'past-day' : ''}`}
                      onClick={() => {
                        if (!past) {
                          setSelectedDate(day.ttmmyyyy);
                          setSelectedStartTime('');
                          setSelectedEndTime('');
                          setAvailableWheelchairs([]);
                        }
                      }}
                      style={past ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                    >
                      <div className="day-name">{day.dayName}</div>
                      <div className="day-date">{day.display}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedDate && (
              <div className="form-section">
                <h2>{t('wr.step2')}</h2>
                <p className="info-text">
                  {t('wr.selectTimeInfo', { date: '' })} <strong>{getSelectedDateDisplay()}</strong>
                </p>
                
                <div className="time-selection">
                  <div className="time-row">
                    <div className="time-field">
                      <label>{t('wr.startTime')}</label>
                      <select 
                        value={selectedStartTime} 
                        onChange={(e) => {
                          setSelectedStartTime(e.target.value);
                          setSelectedEndTime('');
                          setAvailableWheelchairs([]);
                        }}
                        className="time-select"
                      >
                        <option value="">{t('wr.selectStart')}</option>
                        {getTimeOptions(getOktoberfestDays().find(d => d.ttmmyyyy === selectedDate)?.date.getDay(), false).map((time, index) => (
                          <option key={index} value={time.value}>
                            {time.display}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="time-field">
                      <label>{t('wr.endTime')}</label>
                      <select 
                        value={selectedEndTime} 
                        onChange={(e) => {
                          setSelectedEndTime(e.target.value);
                        }}
                        className="time-select"
                        disabled={!selectedStartTime}
                      >
                        <option value="">{t('wr.selectEnd')}</option>
                        {selectedStartTime && getTimeOptions(getOktoberfestDays().find(d => d.ttmmyyyy === selectedDate)?.date.getDay(), true).map((time, index) => (
                          <option key={index} value={time.value}>
                            {time.display}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                {loading && (
                  <div className="text-center mt-3">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p>{t('wr.checkingAvail')}</p>
                  </div>
                )}
              </div>
            )}

            {availableWheelchairs.length > 0 && (
              <div className="form-section">
                <h2>{t('wr.step3')}</h2>
                
                <div className="booking-summary">
                  <h4>{t('wr.selectedPeriod')}</h4>
                  <p><strong>{t('wr.bookingDate')}</strong> {getSelectedDateDisplay()}</p>
                  <p><strong>{t('wr.bookingTime')}</strong> {selectedStartTime} - {selectedEndTime} {t('wr.clock')}</p>
                </div>

                <div className="form-grid" style={{ marginBottom: '20px' }}>
                  <div>
                    <label>{t('wr.name')}</label>
                    <input
                      type="text"
                      value={bookingData.customerName}
                      onChange={(e) => setBookingData({...bookingData, customerName: e.target.value})}
                      placeholder={t('wr.namePlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <label>{t('wr.phoneLbl')}</label>
                    <input
                      type="tel"
                      inputMode="tel"
                      value={bookingData.customerPhone}
                      onChange={(e) => setBookingData({...bookingData, customerPhone: e.target.value})}
                      placeholder={t('wr.phonePlaceholder')}
                    />
                  </div>
                  <div>
                    <label>{t('wr.emailLbl')}</label>
                    <input
                      type="email"
                      inputMode="email"
                      value={bookingData.customerEmail}
                      onChange={(e) => setBookingData({...bookingData, customerEmail: e.target.value})}
                      placeholder="name@beispiel.de"
                    />
                  </div>
                  <div className="full-width">
                    <label>{t('wr.remarksLbl')}</label>
                    <textarea
                      value={bookingData.notes}
                      onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                      placeholder={t('wr.remarksPlaceholder')}
                      rows="3"
                    />
                  </div>
                </div>

                <h3>{t('wr.availableWheelchairs', { count: availableWheelchairs.length })}</h3>
                {/* Gruppiert nach Station */}
                {Object.entries(
                  availableWheelchairs.reduce((groups, wc) => {
                    const key = wc.station_name || t('wr.unknownStation');
                    if (!groups[key]) groups[key] = { wheelchairs: [], address: wc.station_address, mapsUrl: wc.google_maps_url };
                    groups[key].wheelchairs.push(wc);
                    return groups;
                  }, {})
                ).map(([stationName, stationData]) => (
                  <div key={stationName} style={{ marginBottom: '20px' }}>
                    <h4 style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px', marginBottom: '10px' }}>
                      📍 {stationName}
                      <small style={{ fontWeight: 'normal', marginLeft: '10px', color: '#666' }}>
                        {stationData.address}
                        {stationData.mapsUrl && (
                          <a href={stationData.mapsUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '8px' }}>
                            {t('wr.map')}
                          </a>
                        )}
                      </small>
                    </h4>
                    <div className="wheelchairs-grid">
                      {stationData.wheelchairs.map(wheelchair => (
                        <div key={wheelchair.id} className="wheelchair-card">
                          <h4>{t('wr.wheelchairNum', { num: wheelchair.wheelchair_number })}</h4>
                          <p>{t('wr.stationName', { name: stationName })}</p>
                          <button 
                            onClick={() => createBooking(wheelchair.id, wheelchair)}
                            disabled={loading || !bookingData.customerName}
                            className="book-btn"
                            title={!bookingData.customerName ? t('wr.enterName') : ''}
                          >
                            {loading ? t('wr.booking') : t('wr.bookNow')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedDate && selectedStartTime && selectedEndTime && availableWheelchairs.length === 0 && !loading && (
              <div className="no-availability">
                <p>{t('wr.noWheelchairs')}</p>
                <p>{t('wr.chooseOtherTimes')}</p>
              </div>
            )}
          </>
        )}
      </div>
      <ImpressumFooter />
    </div>
  );
};

export default WheelchairRental;
